import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from './AuthContext';
import API, { authFetch } from './api';

export default function PropertyManagement() {
  const { user } = useAuth();
  const [properties, setProperties] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('list');
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [typeFilter, setTypeFilter] = useState('');
  const [buyers, setBuyers] = useState([]);

  // Shared listing-type badge styling. Used here and on PropertyDetail/AgentBoard/MatchBoard.
  const listingTypeBadge = (lt) => {
    if (lt === 'for_sale')           return { label: 'FOR SALE',     cls: 'bg-green-900/50 text-green-300 border-green-600/40' };
    if (lt === 'for_sale_and_lease') return { label: 'SALE & LEASE', cls: 'bg-orange-900/50 text-orange-300 border-orange-600/40' };
    if (lt === 'for_lease')          return { label: 'FOR LEASE',    cls: 'bg-blue-900/50 text-blue-300 border-blue-600/40' };
    return null;
  };

  // PDF upload state
  const [pdfFile, setPdfFile] = useState(null);
  const [pdfUploading, setPdfUploading] = useState(false);
  const [pdfError, setPdfError] = useState('');
  const [pdfDragOver, setPdfDragOver] = useState(false);
  const [autoPopulatedFields, setAutoPopulatedFields] = useState(new Set());
  // Tailwind class to tint auto-populated inputs cyan — used on every always-visible field.
  const autoCls = (field) => autoPopulatedFields.has(field)
    ? ' ring-2 ring-[#00D4FF]/50 ring-offset-1 ring-offset-[#1A2744]'
    : '';

  // Manual form (Commercial Real Estate)
  const emptyForm = {
    // Identity
    mls_number: '', address: '', city: '', state: '', zip: '',
    property_name: '', property_subtype: '',
    property_type: 'office', listing_type: 'for_lease', listing_status: 'active', price: '',
    // Building Specs
    rentable_sqft: '', usable_sqft: '', available_sqft: '', sqft: '',
    ceiling_height_ft: '', stories: '', loading_docks: '',
    building_class: '', freight_elevator: false,
    number_of_units: '', office_percentage: '', tenancy_type: '',
    // Lease
    lease_type: '', lease_rate_sqft: '', cam_charges: '', ti_allowance: '',
    // Investment
    cap_rate: '', noi: '', occupancy_rate: '',
    annual_taxes: '', original_list_price: '',
    investment_highlights: '',
    // Industrial features
    drive_in_doors: '', rail_access: false, column_spacing: '',
    floor_load_capacity: '', power_type: '',
    construction_type: '', roof_type: '',
    // Parking & access
    parking_spaces: '', parking_ratio: '',
    frontage_ft: '', traffic_count: '', cross_streets: '',
    // Zoning & Compliance
    zoning: '', ada_compliant: false, sprinkler_system: false,
    environmental_status: '',
    // Condition & Systems
    year_built: '', year_renovated: '',
    cooling: '', heating: '', building_systems: '', building_amenities: '',
    lot_size_acres: '',
    // Listing broker
    listing_broker_name: '', listing_broker_company: '',
    listing_broker_phone: '', listing_broker_email: '',
    // Location & Description
    county: '', business_park: '', listing_url: '', description: '',
    photo_url: '',
  };
  const [form, setForm] = useState(emptyForm);

  // Broker/super_admin must pick which agent owns the new listing — the backend
  // routes them to /api/properties/for-agent/{agent_id}. Agents create for themselves.
  const isBrokerOrAdmin = user && (user.role === 'broker' || user.role === 'super_admin');
  const [agents, setAgents] = useState([]);
  const [selectedAgentId, setSelectedAgentId] = useState(null);

  const [expandedSections, setExpandedSections] = useState(() => ({
    specs: false,
    // Open Lease Info by default when the listing involves a lease, so CAM / TI / Lease Type are visible immediately.
    lease: emptyForm.listing_type === 'for_lease' || emptyForm.listing_type === 'for_sale_and_lease',
    investment: false,
    industrial: false, access: false,
    zoning: false, condition: false, broker: false, location: false,
  }));
  const toggleSection = (key) => setExpandedSections(s => ({ ...s, [key]: !s[key] }));

  useEffect(() => {
    loadProperties();
    loadBuyers();
  }, [statusFilter]);

  useEffect(() => {
    if (!isBrokerOrAdmin) return;
    (async () => {
      try {
        const res = await authFetch(`${API}/api/agents/brokerage`);
        if (res.ok) {
          const list = await res.json();
          const arr = Array.isArray(list) ? list : [];
          setAgents(arr);
          if (arr.length === 1) setSelectedAgentId(arr[0].id);
        }
      } catch (err) { /* silent — dropdown just won't populate */ }
    })();
  }, [isBrokerOrAdmin]);

  // Auto-expand Lease Info whenever the listing type becomes lease-relevant.
  // We only expand (never auto-collapse) — if the user manually closes it, leave it alone.
  useEffect(() => {
    if (form.listing_type === 'for_lease' || form.listing_type === 'for_sale_and_lease') {
      setExpandedSections(s => s.lease ? s : { ...s, lease: true });
    }
  }, [form.listing_type]);

  const loadProperties = async () => {
    try {
      let url = `${API}/api/properties?limit=200`;
      if (statusFilter) url += `&listing_status=${statusFilter}`;
      const res = await authFetch(url);
      if (res.ok) setProperties(await res.json());
    } catch (err) {
      console.error('Load error:', err);
    } finally {
      setLoading(false);
    }
  };

  const loadBuyers = async () => {
    try {
      const res = await authFetch(`${API}/api/buyers`);
      if (res.ok) setBuyers(await res.json());
    } catch (err) { /* silent */ }
  };

  // --- Manual Entry ---

  const handleCreate = async (e) => {
    e.preventDefault();
    if (isBrokerOrAdmin && !selectedAgentId) {
      setError('Select which agent this listing belongs to');
      return;
    }
    setCreating(true);
    setError('');
    setSuccess('');
    try {
      const body = { ...form };
      // Integers
      ['sqft', 'rentable_sqft', 'usable_sqft', 'available_sqft',
       'stories', 'year_built', 'year_renovated',
       'loading_docks', 'drive_in_doors', 'parking_spaces',
       'number_of_units', 'frontage_ft', 'traffic_count'].forEach(f => {
        if (body[f] !== '' && body[f] != null) body[f] = parseInt(body[f]);
        else delete body[f];
      });
      // Floats
      ['price', 'original_list_price', 'cam_charges', 'ceiling_height_ft',
       'lot_size_acres', 'parking_ratio', 'lease_rate_sqft', 'cap_rate', 'noi',
       'occupancy_rate', 'ti_allowance', 'annual_taxes',
       'office_percentage'].forEach(f => {
        if (body[f] !== '' && body[f] != null) body[f] = parseFloat(body[f]);
        else delete body[f];
      });
      // Strip empty optional text fields so the backend stores NULL rather than ""
      [
        'mls_number', 'listing_url', 'photo_url',
        'property_name', 'property_subtype',
        'county', 'business_park', 'building_class', 'zoning', 'lease_type',
        'cooling', 'heating', 'building_systems', 'building_amenities', 'description',
        'column_spacing', 'floor_load_capacity', 'power_type',
        'construction_type', 'roof_type', 'tenancy_type',
        'environmental_status', 'investment_highlights', 'cross_streets',
        'listing_broker_name', 'listing_broker_company',
        'listing_broker_phone', 'listing_broker_email',
      ].forEach(f => {
        if (body[f] === '' || body[f] == null) delete body[f];
      });

      const url = isBrokerOrAdmin
        ? `${API}/api/properties/for-agent/${selectedAgentId}`
        : `${API}/api/properties`;
      const res = await authFetch(url, {
        method: 'POST',
        body: JSON.stringify(body)
      });
      if (res.ok) {
        const data = await res.json();
        setSuccess(`Property "${data.address}" added successfully`);
        setForm(emptyForm);
        setAutoPopulatedFields(new Set());
        setActiveTab('list');
        loadProperties();
      } else {
        const err = await res.json();
        setError(err.detail || 'Failed to add property');
      }
    } catch (err) {
      setError('Network error');
    } finally {
      setCreating(false);
    }
  };

  // --- PDF Commercial Listing Upload ---

  const handlePdfFile = (file) => {
    setPdfError('');
    if (!file) { setPdfFile(null); return; }
    if (!file.name.toLowerCase().endsWith('.pdf')) {
      setPdfError('Please upload a .pdf file');
      return;
    }
    if (file.size > 10 * 1024 * 1024) {
      setPdfError('PDF too large — max 10MB');
      return;
    }
    setPdfFile(file);
  };

  const handlePdfUpload = async () => {
    if (!pdfFile) return;
    setPdfUploading(true);
    setPdfError('');
    try {
      const fd = new FormData();
      fd.append('file', pdfFile);
      const res = await authFetch(`${API}/api/pdf-import/extract`, {
        method: 'POST',
        body: fd,
      });
      const data = await res.json();
      if (!res.ok) {
        setPdfError(data.detail || 'AI extraction failed');
        setPdfUploading(false);
        return;
      }
      // Merge extracted fields into the manual form, then switch to that tab
      const merged = { ...emptyForm };
      const populated = new Set();
      Object.entries(data.fields).forEach(([k, v]) => {
        if (v !== null && v !== undefined && v !== '') {
          // Booleans stay boolean; everything else is stringified for input bindings
          merged[k] = (typeof v === 'boolean') ? v : String(v);
          populated.add(k);
        }
      });
      setForm(merged);
      setAutoPopulatedFields(populated);
      setPdfFile(null);
      setActiveTab('manual');
      // Auto-expand any collapsible section that has at least one populated field
      const has = (...fields) => fields.some(f => populated.has(f));
      setExpandedSections(s => ({
        specs:       s.specs       || has('rentable_sqft', 'usable_sqft', 'available_sqft', 'sqft', 'ceiling_height_ft', 'stories', 'loading_docks', 'building_class', 'freight_elevator', 'number_of_units', 'office_percentage', 'tenancy_type'),
        lease:       s.lease       || has('lease_type', 'lease_rate_sqft', 'cam_charges', 'ti_allowance'),
        investment:  s.investment  || has('cap_rate', 'noi', 'occupancy_rate', 'annual_taxes', 'investment_highlights'),
        industrial:  s.industrial  || has('drive_in_doors', 'rail_access', 'column_spacing', 'floor_load_capacity', 'power_type', 'construction_type', 'roof_type'),
        access:      s.access      || has('parking_spaces', 'parking_ratio', 'frontage_ft', 'traffic_count', 'cross_streets'),
        zoning:      s.zoning      || has('zoning', 'ada_compliant', 'sprinkler_system', 'environmental_status'),
        condition:   s.condition   || has('year_built', 'year_renovated', 'lot_size_acres', 'cooling', 'heating', 'building_systems', 'building_amenities'),
        broker:      s.broker      || has('listing_broker_name', 'listing_broker_company', 'listing_broker_phone', 'listing_broker_email'),
        location:    s.location    || has('county', 'business_park', 'listing_url', 'description'),
      }));
      setSuccess(`✨ AI extracted ${data.populated_count} of ${data.total_fields} fields from "${data.filename}". Review and save below.`);
    } catch (err) {
      setPdfError('Network error while uploading');
    } finally {
      setPdfUploading(false);
    }
  };

  const handleDelete = async (propId, address) => {
    if (!window.confirm(`Delete "${address}" and all associated grades and scores?`)) return;
    try {
      const res = await authFetch(`${API}/api/properties/${propId}`, { method: 'DELETE' });
      if (res.ok) loadProperties();
    } catch (err) {
      console.error('Delete error:', err);
    }
  };

  const filteredProperties = properties.filter(p => {
    if (typeFilter && p.listing_type !== typeFilter) return false;
    if (!search) return true;
    const q = search.toLowerCase();
    return (
      p.address?.toLowerCase().includes(q) ||
      p.city?.toLowerCase().includes(q) ||
      p.mls_number?.toLowerCase().includes(q) ||
      p.zip?.includes(search)
    );
  });

  const formatPrice = (price) => price ? `$${Number(price).toLocaleString()}` : 'N/A';

  // Reused input className builder — applies cyan ring to AI-populated fields.
  const inputCls = (field) =>
    `w-full px-3 py-2 bg-[#0A0E27] border border-[#546E7A]/50 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-[#00D4FF]${autoCls(field)}`;
  const selectCls = (field) =>
    `w-full px-3 py-2 bg-[#0A0E27] border border-[#546E7A]/50 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-[#00D4FF]${autoCls(field)}`;
  const textareaCls = (field, resize = 'none') =>
    `w-full px-3 py-2 bg-[#0A0E27] border border-[#546E7A]/50 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-[#00D4FF] resize-${resize}${autoCls(field)}`;

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0A0E27] flex items-center justify-center">
        <div className="text-[#00D4FF] text-lg">Loading properties...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0A0E27] pt-8 pb-12">
      <div className="max-w-7xl mx-auto px-4 md:px-6">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:justify-between md:items-center gap-3 md:gap-0 mb-6">
          <div>
            <h1 className="text-xl md:text-2xl font-bold text-white">Property Management</h1>
            <p className="text-[#B0BEC5] text-sm mt-1">{properties.length} properties</p>
          </div>
        </div>

        {/* Messages */}
        {success && (
          <div className="bg-green-900/50 border border-green-600 text-green-200 px-4 py-3 rounded-lg mb-4 text-sm">{success}</div>
        )}
        {error && (
          <div className="bg-red-900/50 border border-red-700 text-red-300 px-4 py-3 rounded-lg mb-4 text-sm">{error}</div>
        )}

        {/* Tabs */}
        <div className="flex gap-1 mb-6 border-b border-[#546E7A]/30 overflow-x-auto">
          <button onClick={() => setActiveTab('list')}
            className={`px-5 py-3 text-sm font-medium border-b-2 -mb-px transition-colors whitespace-nowrap ${activeTab === 'list' ? 'text-[#00D4FF] border-[#00D4FF]' : 'text-gray-400 border-transparent hover:text-white'}`}>
            My Properties
          </button>
          <button onClick={() => setActiveTab('manual')}
            className={`px-5 py-3 text-sm font-medium border-b-2 -mb-px transition-colors whitespace-nowrap ${activeTab === 'manual' ? 'text-[#00D4FF] border-[#00D4FF]' : 'text-gray-400 border-transparent hover:text-white'}`}>
            Manual Entry
          </button>
          <button onClick={() => setActiveTab('pdf')}
            className={`px-5 py-3 text-sm font-medium border-b-2 -mb-px transition-colors whitespace-nowrap ${activeTab === 'pdf' ? 'text-[#00D4FF] border-[#00D4FF]' : 'text-gray-400 border-transparent hover:text-white'}`}>
            ✨ Upload PDF
          </button>
        </div>

        {/* TAB: Property List */}
        {activeTab === 'list' && (
          <>
            {/* Search and Filter */}
            <div className="flex flex-col md:flex-row gap-3 md:gap-4 mb-6">
              <input type="text" value={search} onChange={(e) => setSearch(e.target.value)}
                className="w-full md:flex-1 px-4 py-2 bg-[#1A2744] border border-[#546E7A]/30 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-[#00D4FF]"
                placeholder="Search by address, city, Listing ID, or ZIP..." />
              <select value={typeFilter} onChange={(e) => setTypeFilter(e.target.value)}
                className="w-full md:w-auto px-4 py-2 bg-[#1A2744] border border-[#546E7A]/30 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-[#00D4FF]">
                <option value="">All Types</option>
                <option value="for_lease">For Lease</option>
                <option value="for_sale">For Sale</option>
                <option value="for_sale_and_lease">Sale &amp; Lease</option>
              </select>
              <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}
                className="w-full md:w-auto px-4 py-2 bg-[#1A2744] border border-[#546E7A]/30 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-[#00D4FF]">
                <option value="">All Status</option>
                <option value="active">Active</option>
                <option value="pending">Pending</option>
                <option value="sold">Sold</option>
                <option value="leased">Leased</option>
              </select>
            </div>

            {filteredProperties.length === 0 ? (
              <div className="bg-[#1A2744] rounded-xl border border-[#546E7A]/30 text-center py-16">
                <p className="text-[#B0BEC5] text-lg mb-2">No properties found</p>
                <p className="text-gray-500 mb-4">Add a commercial property manually or upload a brochure PDF to get started.</p>
                <div className="flex gap-3 justify-center flex-wrap">
                  <button onClick={() => setActiveTab('manual')}
                    className="px-6 py-2 bg-[#243B55] text-[#00D4FF] font-semibold rounded-lg hover:bg-[#2d4a6a] transition-colors">
                    Add Property Manually
                  </button>
                  <button onClick={() => setActiveTab('pdf')}
                    className="px-6 py-2 bg-gradient-to-r from-[#00D4FF] to-[#00E676] text-[#0A0E27] font-semibold rounded-lg hover:opacity-90 transition-opacity">
                    ✨ Upload PDF
                  </button>
                </div>
              </div>
            ) : (
              <>
                {/* Mobile cards */}
                <div className="md:hidden space-y-3">
                  {filteredProperties.map(prop => {
                    const lt = listingTypeBadge(prop.listing_type);
                    return (
                    <div key={prop.id} className="bg-[#1A2744] rounded-xl border border-[#546E7A]/30 p-4">
                      <div className="flex justify-between items-start mb-2 gap-2">
                        <a href={`/properties/${prop.id}`} className="text-white font-medium text-sm hover:text-[#00D4FF] min-w-0 break-words">{prop.address}</a>
                        <div className="flex flex-col items-end gap-1 shrink-0">
                          {lt && <span className={`px-2 py-0.5 rounded text-[10px] font-bold border ${lt.cls}`}>{lt.label}</span>}
                          <span className={`px-2 py-0.5 rounded text-xs font-medium ${
                            prop.listing_status === 'active' ? 'bg-green-900/50 text-green-300' :
                            prop.listing_status === 'pending' ? 'bg-yellow-900/50 text-yellow-300' :
                            'bg-red-900/50 text-red-300'
                          }`}>{prop.listing_status}</span>
                        </div>
                      </div>
                      <div className="text-gray-300 text-sm mb-1">{prop.city}, {prop.state}</div>
                      {prop.mls_number && <div className="text-gray-500 text-xs mb-2">Listing ID: {prop.mls_number}</div>}
                      {prop.buyers_matched != null && prop.buyers_matched > 0 && (
                        <div className="text-[#00D4FF] text-xs mb-2">👥 {prop.buyers_matched} client{prop.buyers_matched !== 1 ? 's' : ''} matched</div>
                      )}
                      {(prop.lease_rate_sqft != null || prop.price != null) && (
                        <div className="mb-2">
                          {prop.lease_rate_sqft != null && (
                            <div className="text-[#00E676] font-bold text-lg">${Number(prop.lease_rate_sqft).toFixed(2)}/sqft/yr</div>
                          )}
                          {prop.price != null && (
                            prop.lease_rate_sqft != null
                              ? <div className="text-gray-400 text-xs">Sale: ${Number(prop.price).toLocaleString()}</div>
                              : <div className="text-[#00E676] font-bold text-lg">${Number(prop.price).toLocaleString()}</div>
                          )}
                        </div>
                      )}
                      <div className="flex flex-wrap gap-x-3 gap-y-1 text-gray-400 text-xs mb-3">
                        <span>{prop.property_type ? prop.property_type.replace('_', ' ') : '-'}</span>
                        <span>{(prop.rentable_sqft || prop.sqft) ? (prop.rentable_sqft || prop.sqft).toLocaleString() : '-'} sqft</span>
                        {prop.building_class && <span>Class {prop.building_class}</span>}
                        <span className={`px-2 py-0.5 rounded font-medium ${
                          prop.data_source === 'idx' ? 'bg-cyan-900/50 text-cyan-300' : 'bg-gray-700 text-gray-300'
                        }`}>{prop.data_source === 'idx' ? 'Listing' : 'Manual'}</span>
                      </div>
                      <div className="flex flex-wrap gap-2">
                        {prop.listing_url && (
                          <a href={prop.listing_url} target="_blank" rel="noopener noreferrer"
                            className="px-3 py-1.5 bg-[#243B55] text-[#00D4FF] text-sm font-medium rounded-lg hover:bg-[#2d4a6a]">View Listing</a>
                        )}
                        <button onClick={() => handleDelete(prop.id, prop.address)}
                          className="px-3 py-1.5 bg-[#243B55] text-red-400 text-sm font-medium rounded-lg hover:bg-[#2d4a6a]">Delete</button>
                      </div>
                    </div>
                    );
                  })}
                </div>

                {/* Desktop table */}
                <div className="hidden md:block bg-[#1A2744] rounded-xl border border-[#546E7A]/30 overflow-hidden">
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b border-[#546E7A]/30">
                        <th className="text-left px-4 py-3 text-sm font-medium text-[#B0BEC5]">Address</th>
                        <th className="text-left px-4 py-3 text-sm font-medium text-[#B0BEC5]">City/State</th>
                        <th className="text-right px-4 py-3 text-sm font-medium text-[#B0BEC5]">Price</th>
                        <th className="text-center px-4 py-3 text-sm font-medium text-[#B0BEC5]">Type</th>
                        <th className="text-right px-4 py-3 text-sm font-medium text-[#B0BEC5]">Sqft</th>
                        <th className="text-center px-4 py-3 text-sm font-medium text-[#B0BEC5]">Class</th>
                        <th className="text-center px-4 py-3 text-sm font-medium text-[#B0BEC5]">Source</th>
                        <th className="text-center px-4 py-3 text-sm font-medium text-[#B0BEC5]">Status</th>
                        <th className="text-center px-4 py-3 text-sm font-medium text-[#B0BEC5]">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredProperties.map(prop => {
                        const lt = listingTypeBadge(prop.listing_type);
                        return (
                        <tr key={prop.id} className="border-b border-[#546E7A]/10 hover:bg-[#243B55]/30 transition-colors">
                          <td className="px-4 py-3">
                            <a href={`/properties/${prop.id}`} className="text-white font-medium text-sm hover:text-[#00D4FF]">{prop.address}</a>
                            {prop.mls_number && <div className="text-gray-500 text-xs">Listing ID: {prop.mls_number}</div>}
                            <div className="flex items-center gap-2 mt-1">
                              {lt && <span className={`px-2 py-0.5 rounded text-[10px] font-bold border ${lt.cls}`}>{lt.label}</span>}
                              {prop.buyers_matched != null && prop.buyers_matched > 0 && (
                                <span className="text-[#00D4FF] text-xs">👥 {prop.buyers_matched} matched</span>
                              )}
                            </div>
                          </td>
                          <td className="px-4 py-3 text-gray-300 text-sm">{prop.city}, {prop.state}</td>
                          <td className="px-4 py-3 text-right">
                            {prop.lease_rate_sqft != null && (
                              <div className="text-[#00E676] font-medium text-sm">${Number(prop.lease_rate_sqft).toFixed(2)}/sqft/yr</div>
                            )}
                            {prop.price != null && (
                              prop.lease_rate_sqft != null
                                ? <div className="text-gray-400 text-xs">Sale: ${Number(prop.price).toLocaleString()}</div>
                                : <div className="text-[#00E676] font-medium text-sm">${Number(prop.price).toLocaleString()}</div>
                            )}
                          </td>
                          <td className="px-4 py-3 text-gray-300 text-sm text-center capitalize">{prop.property_type ? prop.property_type.replace('_', ' ') : '-'}</td>
                          <td className="px-4 py-3 text-gray-300 text-sm text-right">{(prop.rentable_sqft || prop.sqft) ? (prop.rentable_sqft || prop.sqft).toLocaleString() : '-'}</td>
                          <td className="px-4 py-3 text-gray-300 text-sm text-center">{prop.building_class || '-'}</td>
                          <td className="px-4 py-3 text-center">
                            <span className={`px-2 py-0.5 rounded text-xs font-medium ${
                              prop.data_source === 'idx' ? 'bg-cyan-900/50 text-cyan-300' : 'bg-gray-700 text-gray-300'
                            }`}>
                              {prop.data_source === 'idx' ? 'Listing' : 'Manual'}
                            </span>
                          </td>
                          <td className="px-4 py-3 text-center">
                            <span className={`px-2 py-0.5 rounded text-xs font-medium ${
                              prop.listing_status === 'active' ? 'bg-green-900/50 text-green-300' :
                              prop.listing_status === 'pending' ? 'bg-yellow-900/50 text-yellow-300' :
                              'bg-red-900/50 text-red-300'
                            }`}>
                              {prop.listing_status}
                            </span>
                          </td>
                          <td className="px-4 py-3 text-center">
                            <div className="flex gap-2 justify-center">
                              {prop.listing_url && (
                                <a href={prop.listing_url} target="_blank" rel="noopener noreferrer"
                                  className="text-[#00D4FF] hover:text-[#00E676] text-sm" title="View listing">👁</a>
                              )}
                              <button onClick={() => handleDelete(prop.id, prop.address)}
                                className="text-gray-600 hover:text-red-400 text-sm" title="Delete">✕</button>
                            </div>
                          </td>
                        </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
                </div>
              </>
            )}
          </>
        )}

        {/* TAB: Upload PDF */}
        {activeTab === 'pdf' && (
          <div className="bg-[#1A2744] rounded-xl border border-[#546E7A]/30 p-4 md:p-6">
            <h2 className="text-lg font-semibold text-white mb-2">Upload a Commercial Listing PDF</h2>
            <p className="text-gray-400 text-sm mb-5">
              Drop in a commercial brochure, offering memorandum, or listing flyer and AI will
              extract address, lease terms, building specs, and 50+ other fields automatically. You'll
              get a chance to review and edit before saving.
            </p>

            {pdfError && (
              <div className="bg-red-900/30 border border-red-700/50 text-red-300 px-4 py-3 rounded-lg text-sm mb-4 flex items-center justify-between flex-wrap gap-2">
                <span>{pdfError}</span>
                <button onClick={() => setPdfError('')} className="text-red-200 underline text-xs hover:text-white">Dismiss</button>
              </div>
            )}

            {!pdfUploading && (
              <div
                onDragOver={(e) => { e.preventDefault(); setPdfDragOver(true); }}
                onDragLeave={() => setPdfDragOver(false)}
                onDrop={(e) => {
                  e.preventDefault();
                  setPdfDragOver(false);
                  const f = e.dataTransfer.files?.[0];
                  if (f) handlePdfFile(f);
                }}
                className={`border-2 border-dashed rounded-xl p-8 md:p-12 text-center transition-colors ${
                  pdfDragOver
                    ? 'border-[#00D4FF] bg-[#00D4FF]/5'
                    : pdfFile
                      ? 'border-[#00E676]/50 bg-[#00E676]/5'
                      : 'border-[#546E7A]/40 bg-[#0A0E27]/30 hover:border-[#546E7A]'
                }`}
              >
                {pdfFile ? (
                  <div>
                    <div className="text-4xl mb-2">📄</div>
                    <div className="text-white font-medium text-sm break-all">{pdfFile.name}</div>
                    <div className="text-gray-500 text-xs mt-1">{(pdfFile.size / 1024).toFixed(0)} KB</div>
                    <div className="flex items-center justify-center gap-3 mt-4 flex-wrap">
                      <button
                        onClick={handlePdfUpload}
                        className="px-5 py-2 bg-gradient-to-r from-[#00D4FF] to-[#00E676] text-[#0A0E27] font-semibold rounded-lg hover:opacity-90 transition-opacity"
                      >
                        ✨ Extract with AI
                      </button>
                      <button
                        onClick={() => setPdfFile(null)}
                        className="px-4 py-2 text-gray-400 hover:text-white text-sm transition-colors"
                      >
                        Choose a different file
                      </button>
                    </div>
                  </div>
                ) : (
                  <div>
                    <div className="text-5xl mb-3">📄</div>
                    <div className="text-white font-medium mb-1">Drop your commercial listing PDF here</div>
                    <div className="text-gray-500 text-sm mb-4">or click below to choose a file</div>
                    <label className="inline-block px-5 py-2 bg-[#243B55] text-[#00D4FF] font-medium rounded-lg hover:bg-[#2d4a6a] cursor-pointer transition-colors">
                      Choose PDF file
                      <input
                        type="file"
                        accept="application/pdf,.pdf"
                        className="hidden"
                        onChange={(e) => handlePdfFile(e.target.files?.[0])}
                      />
                    </label>
                    <div className="text-gray-600 text-xs mt-3">Max 10MB. Text-based PDFs only (scanned images aren't supported yet).</div>
                  </div>
                )}
              </div>
            )}

            {pdfUploading && (
              <div className="border-2 border-[#00D4FF]/40 rounded-xl p-8 md:p-12 text-center bg-[#00D4FF]/5">
                <div className="text-4xl mb-3 animate-pulse">✨</div>
                <div className="text-[#00D4FF] font-medium mb-1">AI is reading your commercial listing...</div>
                <div className="text-gray-500 text-sm">Extracting address, lease rate, sqft, ceiling height, cap rate, and 50+ other fields. This usually takes a few seconds.</div>
              </div>
            )}

            <div className="mt-6 text-gray-500 text-xs">
              <p className="mb-1"><span className="text-gray-400">How this works:</span></p>
              <ol className="list-decimal list-inside space-y-1 text-gray-500">
                <li>Upload a PDF — typically an offering memorandum, broker flyer, or commercial listing brochure.</li>
                <li>AI reads the text and extracts ~55 commercial property fields.</li>
                <li>The Manual Entry tab opens with everything pre-filled. Cyan-tinted fields were auto-populated.</li>
                <li>Review, fix anything wrong, and click Add Property to save.</li>
              </ol>
            </div>
          </div>
        )}

        {/* TAB: Manual Entry */}
        {activeTab === 'manual' && (
          <div className="bg-[#1A2744] rounded-xl border border-[#546E7A]/30 p-4 md:p-6">
            <h2 className="text-lg font-semibold text-white mb-2">Add Commercial Property Manually</h2>
            <p className="text-gray-400 text-sm mb-4">For off-market commercial listings not in the listing data feed.</p>
            {isBrokerOrAdmin && (
              <div className="mb-4 max-w-md">
                <label className="block text-sm text-gray-300 mb-1">Listing Agent *</label>
                <select
                  value={selectedAgentId ?? ''}
                  onChange={(e) => setSelectedAgentId(e.target.value === '' ? null : Number(e.target.value))}
                  className="w-full bg-[#0A0E27] border border-[#546E7A]/50 text-white text-sm rounded-lg px-3 py-2 focus:outline-none focus:ring-1 focus:ring-[#00D4FF]"
                  required
                >
                  <option value="">Select an agent…</option>
                  {agents.map((a) => (
                    <option key={a.id} value={a.id}>{a.name}</option>
                  ))}
                </select>
              </div>
            )}
            <form onSubmit={handleCreate} className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {/* ── BASIC INFO (always visible) ── */}
              <div className="md:col-span-2">
                <label className="block text-sm text-gray-300 mb-1">Address *</label>
                <input type="text" value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })}
                  className={inputCls('address')}
                  placeholder="1500 Industrial Pkwy" required />
              </div>
              <div>
                <label className="block text-sm text-gray-300 mb-1">Listing ID</label>
                <input type="text" value={form.mls_number} onChange={(e) => setForm({ ...form, mls_number: e.target.value })}
                  className={inputCls('mls_number')}
                  placeholder="CRE12345" />
              </div>
              <div>
                <label className="block text-sm text-gray-300 mb-1">Property Name</label>
                <input type="text" value={form.property_name} onChange={(e) => setForm({ ...form, property_name: e.target.value })}
                  className={inputCls('property_name')}
                  placeholder="Cummings Tech Center" />
              </div>
              <div>
                <label className="block text-sm text-gray-300 mb-1">City</label>
                <input type="text" value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })}
                  className={inputCls('city')}
                  placeholder="Madison" />
              </div>
              <div>
                <label className="block text-sm text-gray-300 mb-1">State</label>
                <input type="text" value={form.state} onChange={(e) => setForm({ ...form, state: e.target.value })}
                  className={inputCls('state')}
                  placeholder="AL" />
              </div>
              <div>
                <label className="block text-sm text-gray-300 mb-1">ZIP</label>
                <input type="text" value={form.zip} onChange={(e) => setForm({ ...form, zip: e.target.value })}
                  className={inputCls('zip')}
                  placeholder="35758" />
              </div>
              <div>
                <label className="block text-sm text-gray-300 mb-1">Property Type</label>
                <select value={form.property_type} onChange={(e) => setForm({ ...form, property_type: e.target.value })}
                  className={selectCls('property_type')}>
                  <option value="office">Office</option>
                  <option value="retail">Retail</option>
                  <option value="industrial">Industrial</option>
                  <option value="warehouse">Warehouse</option>
                  <option value="mixed_use">Mixed-Use</option>
                  <option value="flex">Flex</option>
                  <option value="land">Land</option>
                </select>
              </div>
              <div>
                <label className="block text-sm text-gray-300 mb-1">Property Subtype</label>
                <input type="text" value={form.property_subtype} onChange={(e) => setForm({ ...form, property_subtype: e.target.value })}
                  className={inputCls('property_subtype')}
                  placeholder="Medical office, Big-box, etc." />
              </div>
              <div>
                <label className="block text-sm text-gray-300 mb-1">Listing Type</label>
                <select value={form.listing_type} onChange={(e) => setForm({ ...form, listing_type: e.target.value })}
                  className={selectCls('listing_type')}>
                  <option value="for_lease">For Lease</option>
                  <option value="for_sale">For Sale</option>
                  <option value="for_sale_and_lease">Sale &amp; Lease</option>
                </select>
              </div>
              <div>
                <label className="block text-sm text-gray-300 mb-1">Listing Status</label>
                <select value={form.listing_status} onChange={(e) => setForm({ ...form, listing_status: e.target.value })}
                  className={selectCls('listing_status')}>
                  <option value="active">Active</option>
                  <option value="pending">Pending</option>
                  <option value="sold">Sold</option>
                  <option value="leased">Leased</option>
                </select>
              </div>
              {/* Pricing pair — Sale Price + Lease Rate side by side. The one that matches listing_type gets a green accent; the other is dimmed. */}
              <div className="md:col-span-3 grid grid-cols-1 md:grid-cols-2 gap-3">
                {(() => {
                  const saleActive  = form.listing_type === 'for_sale'  || form.listing_type === 'for_sale_and_lease';
                  const leaseActive = form.listing_type === 'for_lease' || form.listing_type === 'for_sale_and_lease';
                  return (
                    <>
                      <div className={`pl-3 border-l-4 transition-colors ${saleActive ? 'border-[#00E676]' : 'border-transparent opacity-50'}`}>
                        <label className="block text-sm text-gray-300 mb-1">
                          Sale Price{saleActive && <span className="text-[#00E676] text-xs ml-1">●</span>}
                        </label>
                        <input type="text" inputMode="numeric" value={form.price} onChange={(e) => setForm({ ...form, price: e.target.value })}
                          className={inputCls('price')}
                          placeholder="2500000" />
                      </div>
                      <div className={`pl-3 border-l-4 transition-colors ${leaseActive ? 'border-[#00E676]' : 'border-transparent opacity-50'}`}>
                        <label className="block text-sm text-gray-300 mb-1">
                          Lease Rate ($/sqft/yr){leaseActive && <span className="text-[#00E676] text-xs ml-1">●</span>}
                        </label>
                        <input type="number" step="0.01" value={form.lease_rate_sqft}
                          onChange={(e) => setForm({ ...form, lease_rate_sqft: e.target.value })}
                          className={inputCls('lease_rate_sqft')}
                          placeholder="22.50" />
                      </div>
                    </>
                  );
                })()}
              </div>

              {/* ── COLLAPSIBLE SECTIONS ── */}
              <div className="md:col-span-3 border-t border-[#546E7A]/20 mt-2 pt-3">
                <p className="text-gray-500 text-xs">Optional details — expand only what applies. Cyan-ringed inputs were AI-populated.</p>
              </div>

              {/* Building Specs */}
              <div className="md:col-span-3">
                <button type="button" onClick={() => toggleSection('specs')}
                  className="w-full flex items-center justify-between px-3 py-2 bg-[#0A0E27]/40 border border-[#546E7A]/30 rounded-lg text-white text-sm hover:bg-[#0A0E27]/60 transition-colors">
                  <span className="font-medium">Building Specs</span>
                  <span className={`transition-transform duration-200 ${expandedSections.specs ? 'rotate-90' : ''}`}>▶</span>
                </button>
              </div>
              {expandedSections.specs && (
                <>
                  <div>
                    <label className="block text-sm text-gray-300 mb-1">Rentable Sqft</label>
                    <input type="number" value={form.rentable_sqft}
                      onChange={(e) => setForm({ ...form, rentable_sqft: e.target.value })}
                      className={inputCls('rentable_sqft')}
                      placeholder="25000" />
                  </div>
                  <div>
                    <label className="block text-sm text-gray-300 mb-1">Usable Sqft</label>
                    <input type="number" value={form.usable_sqft}
                      onChange={(e) => setForm({ ...form, usable_sqft: e.target.value })}
                      className={inputCls('usable_sqft')}
                      placeholder="22500" />
                  </div>
                  <div>
                    <label className="block text-sm text-gray-300 mb-1">Available Sqft</label>
                    <input type="number" value={form.available_sqft}
                      onChange={(e) => setForm({ ...form, available_sqft: e.target.value })}
                      className={inputCls('available_sqft')}
                      placeholder="12000" />
                  </div>
                  <div>
                    <label className="block text-sm text-gray-300 mb-1">Total Sqft</label>
                    <input type="number" value={form.sqft}
                      onChange={(e) => setForm({ ...form, sqft: e.target.value })}
                      className={inputCls('sqft')}
                      placeholder="28000" />
                  </div>
                  <div>
                    <label className="block text-sm text-gray-300 mb-1">Ceiling Height (ft)</label>
                    <input type="number" step="0.5" value={form.ceiling_height_ft}
                      onChange={(e) => setForm({ ...form, ceiling_height_ft: e.target.value })}
                      className={inputCls('ceiling_height_ft')}
                      placeholder="24" />
                  </div>
                  <div>
                    <label className="block text-sm text-gray-300 mb-1">Stories</label>
                    <input type="number" min="1" value={form.stories}
                      onChange={(e) => setForm({ ...form, stories: e.target.value })}
                      className={inputCls('stories')}
                      placeholder="2" />
                  </div>
                  <div>
                    <label className="block text-sm text-gray-300 mb-1">Loading Docks</label>
                    <input type="number" min="0" value={form.loading_docks}
                      onChange={(e) => setForm({ ...form, loading_docks: e.target.value })}
                      className={inputCls('loading_docks')}
                      placeholder="4" />
                  </div>
                  <div>
                    <label className="block text-sm text-gray-300 mb-1">Building Class</label>
                    <select value={form.building_class}
                      onChange={(e) => setForm({ ...form, building_class: e.target.value })}
                      className={selectCls('building_class')}>
                      <option value="">—</option>
                      <option value="A">Class A</option>
                      <option value="B">Class B</option>
                      <option value="C">Class C</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm text-gray-300 mb-1">Freight Elevator</label>
                    <label className={`flex items-center gap-2 px-3 py-2 bg-[#0A0E27] border border-[#546E7A]/50 rounded-lg text-white cursor-pointer${autoCls('freight_elevator')}`}>
                      <input type="checkbox" checked={form.freight_elevator}
                        onChange={(e) => setForm({ ...form, freight_elevator: e.target.checked })}
                        className="w-4 h-4 accent-[#00D4FF]" />
                      <span className="text-sm">Has freight elevator</span>
                    </label>
                  </div>
                  <div>
                    <label className="block text-sm text-gray-300 mb-1">Number of Units</label>
                    <input type="number" min="1" value={form.number_of_units}
                      onChange={(e) => setForm({ ...form, number_of_units: e.target.value })}
                      className={inputCls('number_of_units')}
                      placeholder="3" />
                  </div>
                  <div>
                    <label className="block text-sm text-gray-300 mb-1">Office Percentage (%)</label>
                    <input type="number" step="1" min="0" max="100" value={form.office_percentage}
                      onChange={(e) => setForm({ ...form, office_percentage: e.target.value })}
                      className={inputCls('office_percentage')}
                      placeholder="20" />
                  </div>
                  <div>
                    <label className="block text-sm text-gray-300 mb-1">Tenancy</label>
                    <select value={form.tenancy_type}
                      onChange={(e) => setForm({ ...form, tenancy_type: e.target.value })}
                      className={selectCls('tenancy_type')}>
                      <option value="">—</option>
                      <option value="single">Single-tenant</option>
                      <option value="multi">Multi-tenant</option>
                    </select>
                  </div>
                </>
              )}

              {/* Lease Info */}
              <div className="md:col-span-3">
                <button type="button" onClick={() => toggleSection('lease')}
                  className="w-full flex items-center justify-between px-3 py-2 bg-[#0A0E27]/40 border border-[#546E7A]/30 rounded-lg text-white text-sm hover:bg-[#0A0E27]/60 transition-colors">
                  <span className="font-medium">Lease Info</span>
                  <span className={`transition-transform duration-200 ${expandedSections.lease ? 'rotate-90' : ''}`}>▶</span>
                </button>
              </div>
              {expandedSections.lease && (
                <>
                  <div>
                    <label className="block text-sm text-gray-300 mb-1">Lease Type</label>
                    <select value={form.lease_type}
                      onChange={(e) => setForm({ ...form, lease_type: e.target.value })}
                      className={selectCls('lease_type')}>
                      <option value="">—</option>
                      <option value="nnn">NNN (Triple Net)</option>
                      <option value="gross">Gross</option>
                      <option value="modified_gross">Modified Gross</option>
                      <option value="full_service">Full Service</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm text-gray-300 mb-1">CAM Charges ($/sqft)</label>
                    <input type="text" inputMode="numeric" value={form.cam_charges}
                      onChange={(e) => setForm({ ...form, cam_charges: e.target.value })}
                      className={inputCls('cam_charges')}
                      placeholder="4.25" />
                  </div>
                  <div>
                    <label className="block text-sm text-gray-300 mb-1">TI Allowance ($)</label>
                    <input type="text" inputMode="numeric" value={form.ti_allowance}
                      onChange={(e) => setForm({ ...form, ti_allowance: e.target.value })}
                      className={inputCls('ti_allowance')}
                      placeholder="50000" />
                  </div>
                </>
              )}

              {/* Investment */}
              <div className="md:col-span-3">
                <button type="button" onClick={() => toggleSection('investment')}
                  className="w-full flex items-center justify-between px-3 py-2 bg-[#0A0E27]/40 border border-[#546E7A]/30 rounded-lg text-white text-sm hover:bg-[#0A0E27]/60 transition-colors">
                  <span className="font-medium">Investment</span>
                  <span className={`transition-transform duration-200 ${expandedSections.investment ? 'rotate-90' : ''}`}>▶</span>
                </button>
              </div>
              {expandedSections.investment && (
                <>
                  <div>
                    <label className="block text-sm text-gray-300 mb-1">Cap Rate (%)</label>
                    <input type="number" step="0.01" value={form.cap_rate}
                      onChange={(e) => setForm({ ...form, cap_rate: e.target.value })}
                      className={inputCls('cap_rate')}
                      placeholder="6.5" />
                  </div>
                  <div>
                    <label className="block text-sm text-gray-300 mb-1">NOI ($)</label>
                    <input type="text" inputMode="numeric" value={form.noi}
                      onChange={(e) => setForm({ ...form, noi: e.target.value })}
                      className={inputCls('noi')}
                      placeholder="162500" />
                  </div>
                  <div>
                    <label className="block text-sm text-gray-300 mb-1">Occupancy Rate (%)</label>
                    <input type="number" step="0.1" value={form.occupancy_rate}
                      onChange={(e) => setForm({ ...form, occupancy_rate: e.target.value })}
                      className={inputCls('occupancy_rate')}
                      placeholder="92.5" />
                  </div>
                  <div>
                    <label className="block text-sm text-gray-300 mb-1">Annual Taxes ($)</label>
                    <input type="text" inputMode="numeric" value={form.annual_taxes}
                      onChange={(e) => setForm({ ...form, annual_taxes: e.target.value })}
                      className={inputCls('annual_taxes')}
                      placeholder="38500" />
                  </div>
                  <div>
                    <label className="block text-sm text-gray-300 mb-1">Original List Price</label>
                    <input type="text" inputMode="numeric" value={form.original_list_price}
                      onChange={(e) => setForm({ ...form, original_list_price: e.target.value })}
                      className={inputCls('original_list_price')}
                      placeholder="2750000" />
                  </div>
                  <div className="md:col-span-3">
                    <label className="block text-sm text-gray-300 mb-1">Investment Highlights</label>
                    <textarea value={form.investment_highlights}
                      onChange={(e) => setForm({ ...form, investment_highlights: e.target.value })}
                      className={textareaCls('investment_highlights', 'y')}
                      rows={3}
                      placeholder="Stabilized NOI, credit tenant, value-add potential, below-market rents…" />
                  </div>
                </>
              )}

              {/* Industrial Features */}
              <div className="md:col-span-3">
                <button type="button" onClick={() => toggleSection('industrial')}
                  className="w-full flex items-center justify-between px-3 py-2 bg-[#0A0E27]/40 border border-[#546E7A]/30 rounded-lg text-white text-sm hover:bg-[#0A0E27]/60 transition-colors">
                  <span className="font-medium">Industrial Features</span>
                  <span className={`transition-transform duration-200 ${expandedSections.industrial ? 'rotate-90' : ''}`}>▶</span>
                </button>
              </div>
              {expandedSections.industrial && (
                <>
                  <div>
                    <label className="block text-sm text-gray-300 mb-1">Drive-in Doors</label>
                    <input type="number" min="0" value={form.drive_in_doors}
                      onChange={(e) => setForm({ ...form, drive_in_doors: e.target.value })}
                      className={inputCls('drive_in_doors')}
                      placeholder="2" />
                  </div>
                  <div>
                    <label className="block text-sm text-gray-300 mb-1">Rail Access</label>
                    <label className={`flex items-center gap-2 px-3 py-2 bg-[#0A0E27] border border-[#546E7A]/50 rounded-lg text-white cursor-pointer${autoCls('rail_access')}`}>
                      <input type="checkbox" checked={form.rail_access}
                        onChange={(e) => setForm({ ...form, rail_access: e.target.checked })}
                        className="w-4 h-4 accent-[#00D4FF]" />
                      <span className="text-sm">Rail spur on-site</span>
                    </label>
                  </div>
                  <div>
                    <label className="block text-sm text-gray-300 mb-1">Column Spacing</label>
                    <input type="text" value={form.column_spacing}
                      onChange={(e) => setForm({ ...form, column_spacing: e.target.value })}
                      className={inputCls('column_spacing')}
                      placeholder="40 x 40" />
                  </div>
                  <div>
                    <label className="block text-sm text-gray-300 mb-1">Floor Load Capacity</label>
                    <input type="text" value={form.floor_load_capacity}
                      onChange={(e) => setForm({ ...form, floor_load_capacity: e.target.value })}
                      className={inputCls('floor_load_capacity')}
                      placeholder="250 psf" />
                  </div>
                  <div>
                    <label className="block text-sm text-gray-300 mb-1">Power</label>
                    <input type="text" value={form.power_type}
                      onChange={(e) => setForm({ ...form, power_type: e.target.value })}
                      className={inputCls('power_type')}
                      placeholder="3-phase 480V, 1200A" />
                  </div>
                  <div>
                    <label className="block text-sm text-gray-300 mb-1">Construction Type</label>
                    <input type="text" value={form.construction_type}
                      onChange={(e) => setForm({ ...form, construction_type: e.target.value })}
                      className={inputCls('construction_type')}
                      placeholder="Tilt-up concrete, steel frame…" />
                  </div>
                  <div>
                    <label className="block text-sm text-gray-300 mb-1">Roof Type</label>
                    <input type="text" value={form.roof_type}
                      onChange={(e) => setForm({ ...form, roof_type: e.target.value })}
                      className={inputCls('roof_type')}
                      placeholder="TPO membrane, 2018 replaced" />
                  </div>
                </>
              )}

              {/* Parking & Site Access */}
              <div className="md:col-span-3">
                <button type="button" onClick={() => toggleSection('access')}
                  className="w-full flex items-center justify-between px-3 py-2 bg-[#0A0E27]/40 border border-[#546E7A]/30 rounded-lg text-white text-sm hover:bg-[#0A0E27]/60 transition-colors">
                  <span className="font-medium">Parking & Site Access</span>
                  <span className={`transition-transform duration-200 ${expandedSections.access ? 'rotate-90' : ''}`}>▶</span>
                </button>
              </div>
              {expandedSections.access && (
                <>
                  <div>
                    <label className="block text-sm text-gray-300 mb-1">Parking Spaces</label>
                    <input type="number" min="0" value={form.parking_spaces}
                      onChange={(e) => setForm({ ...form, parking_spaces: e.target.value })}
                      className={inputCls('parking_spaces')}
                      placeholder="125" />
                  </div>
                  <div>
                    <label className="block text-sm text-gray-300 mb-1">Parking Ratio (per 1000 sqft)</label>
                    <input type="number" step="0.1" value={form.parking_ratio}
                      onChange={(e) => setForm({ ...form, parking_ratio: e.target.value })}
                      className={inputCls('parking_ratio')}
                      placeholder="4.5" />
                  </div>
                  <div>
                    <label className="block text-sm text-gray-300 mb-1">Frontage (ft)</label>
                    <input type="number" min="0" value={form.frontage_ft}
                      onChange={(e) => setForm({ ...form, frontage_ft: e.target.value })}
                      className={inputCls('frontage_ft')}
                      placeholder="220" />
                  </div>
                  <div>
                    <label className="block text-sm text-gray-300 mb-1">Traffic Count (veh/day)</label>
                    <input type="number" min="0" value={form.traffic_count}
                      onChange={(e) => setForm({ ...form, traffic_count: e.target.value })}
                      className={inputCls('traffic_count')}
                      placeholder="28500" />
                  </div>
                  <div className="md:col-span-3">
                    <label className="block text-sm text-gray-300 mb-1">Cross Streets</label>
                    <input type="text" value={form.cross_streets}
                      onChange={(e) => setForm({ ...form, cross_streets: e.target.value })}
                      className={inputCls('cross_streets')}
                      placeholder="Madison Blvd & Wall-Triana Hwy" />
                  </div>
                </>
              )}

              {/* Zoning & Compliance */}
              <div className="md:col-span-3">
                <button type="button" onClick={() => toggleSection('zoning')}
                  className="w-full flex items-center justify-between px-3 py-2 bg-[#0A0E27]/40 border border-[#546E7A]/30 rounded-lg text-white text-sm hover:bg-[#0A0E27]/60 transition-colors">
                  <span className="font-medium">Zoning & Compliance</span>
                  <span className={`transition-transform duration-200 ${expandedSections.zoning ? 'rotate-90' : ''}`}>▶</span>
                </button>
              </div>
              {expandedSections.zoning && (
                <>
                  <div>
                    <label className="block text-sm text-gray-300 mb-1">Zoning</label>
                    <input type="text" value={form.zoning}
                      onChange={(e) => setForm({ ...form, zoning: e.target.value })}
                      className={inputCls('zoning')}
                      placeholder="M-1, C-2, B-3, etc." />
                  </div>
                  <div>
                    <label className="block text-sm text-gray-300 mb-1">ADA Compliant</label>
                    <label className={`flex items-center gap-2 px-3 py-2 bg-[#0A0E27] border border-[#546E7A]/50 rounded-lg text-white cursor-pointer${autoCls('ada_compliant')}`}>
                      <input type="checkbox" checked={form.ada_compliant}
                        onChange={(e) => setForm({ ...form, ada_compliant: e.target.checked })}
                        className="w-4 h-4 accent-[#00D4FF]" />
                      <span className="text-sm">ADA compliant</span>
                    </label>
                  </div>
                  <div>
                    <label className="block text-sm text-gray-300 mb-1">Sprinkler System</label>
                    <label className={`flex items-center gap-2 px-3 py-2 bg-[#0A0E27] border border-[#546E7A]/50 rounded-lg text-white cursor-pointer${autoCls('sprinkler_system')}`}>
                      <input type="checkbox" checked={form.sprinkler_system}
                        onChange={(e) => setForm({ ...form, sprinkler_system: e.target.checked })}
                        className="w-4 h-4 accent-[#00D4FF]" />
                      <span className="text-sm">Sprinkler system</span>
                    </label>
                  </div>
                  <div className="md:col-span-3">
                    <label className="block text-sm text-gray-300 mb-1">Environmental Status</label>
                    <input type="text" value={form.environmental_status}
                      onChange={(e) => setForm({ ...form, environmental_status: e.target.value })}
                      className={inputCls('environmental_status')}
                      placeholder="Phase I clean (2024), no recognized environmental conditions" />
                  </div>
                </>
              )}

              {/* Condition */}
              <div className="md:col-span-3">
                <button type="button" onClick={() => toggleSection('condition')}
                  className="w-full flex items-center justify-between px-3 py-2 bg-[#0A0E27]/40 border border-[#546E7A]/30 rounded-lg text-white text-sm hover:bg-[#0A0E27]/60 transition-colors">
                  <span className="font-medium">Condition</span>
                  <span className={`transition-transform duration-200 ${expandedSections.condition ? 'rotate-90' : ''}`}>▶</span>
                </button>
              </div>
              {expandedSections.condition && (
                <>
                  <div>
                    <label className="block text-sm text-gray-300 mb-1">Year Built</label>
                    <input type="number" value={form.year_built}
                      onChange={(e) => setForm({ ...form, year_built: e.target.value })}
                      className={inputCls('year_built')}
                      placeholder="1998" />
                  </div>
                  <div>
                    <label className="block text-sm text-gray-300 mb-1">Year Renovated</label>
                    <input type="number" value={form.year_renovated}
                      onChange={(e) => setForm({ ...form, year_renovated: e.target.value })}
                      className={inputCls('year_renovated')}
                      placeholder="2018" />
                  </div>
                  <div>
                    <label className="block text-sm text-gray-300 mb-1">Lot Size (acres)</label>
                    <input type="number" step="0.01" value={form.lot_size_acres}
                      onChange={(e) => setForm({ ...form, lot_size_acres: e.target.value })}
                      className={inputCls('lot_size_acres')}
                      placeholder="2.5" />
                  </div>
                  <div>
                    <label className="block text-sm text-gray-300 mb-1">Cooling</label>
                    <input type="text" value={form.cooling}
                      onChange={(e) => setForm({ ...form, cooling: e.target.value })}
                      className={inputCls('cooling')}
                      placeholder="Central HVAC, rooftop units" />
                  </div>
                  <div>
                    <label className="block text-sm text-gray-300 mb-1">Heating</label>
                    <input type="text" value={form.heating}
                      onChange={(e) => setForm({ ...form, heating: e.target.value })}
                      className={inputCls('heating')}
                      placeholder="Gas forced air" />
                  </div>
                  <div className="md:col-span-3">
                    <label className="block text-sm text-gray-300 mb-1">Building Systems</label>
                    <textarea value={form.building_systems}
                      onChange={(e) => setForm({ ...form, building_systems: e.target.value })}
                      className={textareaCls('building_systems', 'none')}
                      rows={2}
                      placeholder="Roof type, electrical (3-phase 480V), plumbing, fire alarm, BMS..." />
                  </div>
                  <div className="md:col-span-3">
                    <label className="block text-sm text-gray-300 mb-1">Building Amenities</label>
                    <textarea value={form.building_amenities}
                      onChange={(e) => setForm({ ...form, building_amenities: e.target.value })}
                      className={textareaCls('building_amenities', 'none')}
                      rows={2}
                      placeholder="Conference center, fitness, on-site cafe, security, EV charging..." />
                  </div>
                </>
              )}

              {/* Listing Broker */}
              <div className="md:col-span-3">
                <button type="button" onClick={() => toggleSection('broker')}
                  className="w-full flex items-center justify-between px-3 py-2 bg-[#0A0E27]/40 border border-[#546E7A]/30 rounded-lg text-white text-sm hover:bg-[#0A0E27]/60 transition-colors">
                  <span className="font-medium">Listing Broker</span>
                  <span className={`transition-transform duration-200 ${expandedSections.broker ? 'rotate-90' : ''}`}>▶</span>
                </button>
              </div>
              {expandedSections.broker && (
                <>
                  <div>
                    <label className="block text-sm text-gray-300 mb-1">Broker Name</label>
                    <input type="text" value={form.listing_broker_name}
                      onChange={(e) => setForm({ ...form, listing_broker_name: e.target.value })}
                      className={inputCls('listing_broker_name')}
                      placeholder="Jordan Mills" />
                  </div>
                  <div>
                    <label className="block text-sm text-gray-300 mb-1">Broker Company</label>
                    <input type="text" value={form.listing_broker_company}
                      onChange={(e) => setForm({ ...form, listing_broker_company: e.target.value })}
                      className={inputCls('listing_broker_company')}
                      placeholder="Summit Commercial Realty" />
                  </div>
                  <div>
                    <label className="block text-sm text-gray-300 mb-1">Broker Phone</label>
                    <input type="tel" value={form.listing_broker_phone}
                      onChange={(e) => setForm({ ...form, listing_broker_phone: e.target.value })}
                      className={inputCls('listing_broker_phone')}
                      placeholder="(256) 555-0142" />
                  </div>
                  <div>
                    <label className="block text-sm text-gray-300 mb-1">Broker Email</label>
                    <input type="email" value={form.listing_broker_email}
                      onChange={(e) => setForm({ ...form, listing_broker_email: e.target.value })}
                      className={inputCls('listing_broker_email')}
                      placeholder="jordan@summitcre.com" />
                  </div>
                </>
              )}

              {/* Location & Description */}
              <div className="md:col-span-3">
                <button type="button" onClick={() => toggleSection('location')}
                  className="w-full flex items-center justify-between px-3 py-2 bg-[#0A0E27]/40 border border-[#546E7A]/30 rounded-lg text-white text-sm hover:bg-[#0A0E27]/60 transition-colors">
                  <span className="font-medium">Location & Description</span>
                  <span className={`transition-transform duration-200 ${expandedSections.location ? 'rotate-90' : ''}`}>▶</span>
                </button>
              </div>
              {expandedSections.location && (
                <>
                  <div>
                    <label className="block text-sm text-gray-300 mb-1">County</label>
                    <input type="text" value={form.county}
                      onChange={(e) => setForm({ ...form, county: e.target.value })}
                      className={inputCls('county')}
                      placeholder="Madison" />
                  </div>
                  <div>
                    <label className="block text-sm text-gray-300 mb-1">Business Park</label>
                    <input type="text" value={form.business_park}
                      onChange={(e) => setForm({ ...form, business_park: e.target.value })}
                      className={inputCls('business_park')}
                      placeholder="Cummings Research Park" />
                  </div>
                  <div className="md:col-span-3">
                    <label className="block text-sm text-gray-300 mb-1">Listing URL</label>
                    <input type="url" value={form.listing_url}
                      onChange={(e) => setForm({ ...form, listing_url: e.target.value })}
                      className={inputCls('listing_url')}
                      placeholder="https://loopnet.com/listing/12345" />
                  </div>
                  <div className="md:col-span-3">
                    <label className="block text-sm text-gray-300 mb-1">Description</label>
                    <textarea value={form.description}
                      onChange={(e) => setForm({ ...form, description: e.target.value })}
                      className={textareaCls('description', 'y')}
                      rows={5}
                      placeholder="Full listing description, marketing copy, special features…" />
                  </div>
                </>
              )}

              <div className="md:col-span-3">
                <button type="submit" disabled={creating}
                  className="px-6 py-2 bg-[#00E676] text-[#0A0E27] font-semibold rounded-lg hover:bg-[#00C853] disabled:opacity-50 transition-colors">
                  {creating ? 'Adding...' : 'Add Property'}
                </button>
              </div>
            </form>
          </div>
        )}
      </div>
    </div>
  );
}
